import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  SelectPicker,
  InputNumber,
  Input,
  Checkbox,
  Message,
  useToaster,
  Loader,
  AutoComplete,
  Badge,
  Panel,
} from 'rsuite';
import { isMobile } from 'react-device-detect';
import authFetch, { API_BASE } from '../../helpers/authFetch';
import './AddCardSidePanelModal.css';
import { CONDITION_OPTIONS, FINISH_OPTIONS } from './CardEntryFormOptions';
import { usePreferences } from '../Preferences/PreferencesContext';
import { isDoubleFaced, getSmallImageUri, getOracleText, getManaCost } from '../../helpers/cardHelpers';
import { getDeckTypeChecks } from '../../helpers/deckTypeHelpers';
import { DECK_FORMAT_RULES } from '../../helpers/deckRules';
import { validateCollectionForDeck } from '../../helpers/collectionValidation';
import { OWNERSHIP_DEFAULTS, CARD_FORM_DEFAULTS, getOwnershipDefault } from '../../helpers/cardAddDefaults';

const SPACING = {
  containerPadding: 16,
  sectionGap: 16,
  itemPadding: 12,
  cardDetailsMinWidth: 300,
};

const LAYOUT = {
  leftColumnWidth: 140,
  detailsColumnFlex: 1,
};

const FONT = {
  manaText: 14,
  cardTitle: 18,
  typeLineSmall: 13,
  oracleSmall: 12,
};

const PRINTING_IMAGE = {
  width: 100,
  height: 140,
  borderRadius: 4,
};

const COLLECTION_TYPE_LABELS = {
  DECK: 'Deck',
  TRADE_BINDER: 'Trade Binder',
  WISHLIST: 'Wish List',
  COLLECTION: 'My Collection',
};

const createInitialFormData = () => ({
  collection_id: null,
  scryfall_id: null,
  ...CARD_FORM_DEFAULTS,
  is_not_owned: !OWNERSHIP_DEFAULTS.TRADE_BINDER.is_owned,
});

const AddCardSidePanelModal = ({ open, onClose, onSuccess, preSelectedCollectionId }) => {
  const { preferences } = usePreferences();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedPrinting, setSelectedPrinting] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(-1);

  const [formData, setFormData] = useState(createInitialFormData());
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [printings, setPrintings] = useState([]);
  const [loadingPrintings, setLoadingPrintings] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const toaster = useToaster();
  const searchInputRef = useRef(null);
  const searchAbortControllerRef = useRef(null);

  // Fetch collections on mount
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchCollections = async () => {
      setLoadingCollections(true);
      try {
        const response = await authFetch('/collections');
        if (response.ok && !cancelled) {
          const data = await response.json();
          setCollections(data);
          if (preSelectedCollectionId) {
            const selectedCol = data.find((c) => c.id === preSelectedCollectionId);
            setFormData((prev) => {
              const next = { ...prev, collection_id: preSelectedCollectionId };
              if (selectedCol?.type) {
                const defaults = getOwnershipDefault(selectedCol.type);
                next.is_not_owned = !defaults.is_owned;
              }
              return next;
            });
          }
        } else if (!cancelled) {
          toaster.push(
            <Message type="error" showIcon closable>
              Failed to load collections
            </Message>,
            { placement: 'topCenter', duration: 4000 }
          );
        }
      } catch (err) {
        if (!cancelled) {
          toaster.push(
            <Message type="error" showIcon closable>
              Failed to load collections
            </Message>,
            { placement: 'topCenter', duration: 4000 }
          );
        }
      } finally {
        if (!cancelled) setLoadingCollections(false);
      }
    };

    fetchCollections();
    return () => { cancelled = true; };
  }, [open, preSelectedCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search input
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        setSearchData([]);
        setSelectedCard(null);
        setSelectedPrinting(null);
        setSelectedCardIndex(-1);
        return;
      }

      // Cancel previous search request if still pending
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;

      setSearchLoading(true);
      try {
        const response = await fetch(`${API_BASE}/cards/search/${encodeURIComponent(searchTerm)}`, {
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        const results = await response.text();
        const parsed = JSON.parse(results);
        setSearchResults(parsed);
        setSearchData(parsed.map(card => card.name));
        if (parsed.length > 0) {
          selectCard(parsed[0]);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Search error:', err);
        setSearchResults([]);
        setSearchData([]);
        toaster.push(
          <Message type="error" showIcon closable>
            Search failed. Please try a different term
          </Message>,
          { placement: 'topCenter', duration: 4000 }
        );
      } finally {
        setSearchLoading(false);
      }
    };

    const timer = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, toaster]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch printings when card changes
  useEffect(() => {
    if (!open || !selectedCard?.id) {
      setPrintings([]);
      return;
    }

    let cancelled = false;

    const fetchPrintings = async () => {
      setLoadingPrintings(true);
      try {
        const response = await authFetch(`/cards/${selectedCard.id}/printings`);
        if (!response.ok) {
          throw new Error(`Failed to load printings: ${response.status}`);
        }
        let data;
        try {
          data = await response.json();
        } catch (parseErr) {
          throw new Error('Invalid printings response format');
        }
        if (!cancelled) {
          setPrintings(data);
          if (data.length > 0) {
            setSelectedPrinting(data[0]);
            setField('scryfall_id', data[0].id);
          } else {
            toaster.push(
              <Message type="warning" showIcon closable>
                No printings found for this card
              </Message>,
              { placement: 'topCenter', duration: 3000 }
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Printings fetch error:', err);
          setPrintings([]);
          toaster.push(
            <Message type="error" showIcon closable>
              Failed to load printings. Please try selecting another card
            </Message>,
            { placement: 'topCenter', duration: 4000 }
          );
        }
      } finally {
        if (!cancelled) setLoadingPrintings(false);
      }
    };

    fetchPrintings();
    return () => { cancelled = true; };
  }, [selectedCard?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate collection when it changes
  useEffect(() => {
    if (formData.collection_id && selectedCard) {
      validateCollectionForDeck(formData.collection_id, selectedCard, collections, setWarnings);
    } else {
      setWarnings([]);
    }
  }, [formData.collection_id, selectedCard?.id, collections]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCollection = collections.find((c) => c.id === formData.collection_id);
  const { isDeck, isMyCollection, isCommander, isOathbreaker, isBrawl } = getDeckTypeChecks(selectedCollection);
  // The Sideboard checkbox is shown exactly for formats that actually have a
  // sideboard (sideboardSize > 0): STANDARD/MODERN/LEGACY/VINTAGE/PIONEER/
  // PAUPER/PLANAR_STANDARD. Hidden for COMMANDER/OATHBREAKER/BRAWL/STANDARD_BRAWL
  // (sideboardSize: 0) and for DRAFT/OTHER (sideboardSize: null -> no tracked
  // sideboard here).
  const hasSideboard = isDeck && (DECK_FORMAT_RULES[selectedCollection?.deck_type]?.sideboardSize ?? 0) > 0;

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedCard(null);
    setSelectedPrinting(null);
    setFormData(createInitialFormData());
    setError('');
    setWarnings([]);
    setShowMoreOptions(false);
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const selectCard = (card) => {
    setSelectedCard(card);
    setSelectedCardIndex(searchResults.findIndex(c => c.id === card.id));
  };

  const handleSearchResultSelect = (value) => {
    const card = searchResults.find(c => c.name === value);
    if (card) {
      selectCard(card);
    }
  };

  const handleSelectPrinting = (printing) => {
    setSelectedPrinting(printing);
    setField('scryfall_id', printing.id);
  };

  const handleKeyDown = (e) => {
    if (!selectedCard) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = Math.min(selectedCardIndex + 1, searchResults.length - 1);
      selectCard(searchResults[nextIdx]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIdx = Math.max(selectedCardIndex - 1, 0);
      selectCard(searchResults[prevIdx]);
    } else if (e.key === 'Enter' && formData.collection_id) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'collection_id') {
        next.is_commander = false;
        next.is_sideboard = false;
        next.is_signature_spell = false;
        if (value) {
          const col = collections.find((c) => c.id === value);
          if (col?.type) {
            const defaults = getOwnershipDefault(col.type);
            next.is_not_owned = !defaults.is_owned;
          }
        } else {
          setWarnings([]);
        }
      }
      return next;
    });
  };


  const handleSubmit = async () => {
    setError('');

    if (!formData.collection_id) {
      setError('Please select a collection');
      return;
    }

    if (!selectedCard || !selectedPrinting) {
      setError('Please select a card and printing');
      return;
    }

    setSubmitting(true);
    const cardName = selectedCard.name;
    const collectionName = selectedCollection?.name || 'collection';

    try {
      const body = {
        scryfall_id: selectedPrinting.id,
        quantity: formData.quantity,
        condition: formData.condition,
        finish: formData.finish,
      };

      if (formData.purchase_price !== '') {
        body.purchase_price = parseFloat(formData.purchase_price);
      }

      if (formData.notes.trim()) {
        body.notes = formData.notes.trim();
      }

      if (isCommander) {
        body.is_commander = formData.is_commander;
      }

      if (isOathbreaker) {
        body.is_commander = formData.is_commander;
        body.is_signature_spell = formData.is_signature_spell;
      }

      if (isBrawl) {
        body.is_commander = formData.is_commander;
      }

      if (hasSideboard) {
        body.is_sideboard = formData.is_sideboard;
      }

      body.is_proxy = formData.is_proxy;
      body.is_not_owned = formData.is_not_owned;

      const response = await authFetch(`/collections/${formData.collection_id}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to add card');
        setSubmitting(false);
        return;
      }

      toaster.push(
        <Message type="success" showIcon closable>
          <strong>{cardName}</strong> added to <strong>{collectionName}</strong>
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );

      setFormData((prev) => {
        const defaults = getOwnershipDefault(selectedCollection?.type);
        return {
          ...prev,
          ...CARD_FORM_DEFAULTS,
          is_not_owned: !defaults.is_owned,
        };
      });
    } catch (err) {
      const errorMsg = 'Unable to connect to server';
      setError(errorMsg);
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMsg}
        </Message>,
        { placement: 'topCenter', duration: 4000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const collectionOptions = collections.map((c) => ({
    label: c.type === 'COLLECTION' ? c.name : `${c.name} (${COLLECTION_TYPE_LABELS[c.type] || c.type})`,
    value: c.id,
  }));

  const renderSearchMenuItem = (label, item) => {
    const card = searchResults.find(c => c.name === item.value);
    const thumb = card?.image_uris?.small || card?.card_faces?.[0]?.image_uris?.small;
    const isDFC = card && isDoubleFaced(card);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
        {thumb && (
          <img src={thumb} alt={card.name} style={{ height: 28, borderRadius: 2, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 }}>
            {label}
            {isDFC && (
              <Badge content="DFC" style={{ backgroundColor: '#6c757d', fontSize: '10px' }} />
            )}
          </div>
          {card?.set_name && (
            <div style={{ fontSize: 11, color: '#aaa' }}>{card.set_name}</div>
          )}
        </div>
      </div>
    );
  };

  const renderSearchMenu = (menu) => (
    <div
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => {
        if (e.target.tagName === 'DIV' || e.target.tagName === 'IMG') {
          e.preventDefault();
        }
      }}
    >
      {menu}
    </div>
  );


  const oracleTextBgColor = preferences.theme === 'light' ? '#f5f5f5' : '#2a2a3e';
  const oracleTextColor = preferences.theme === 'light' ? '#333' : '#e0e0e0';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={isMobile ? 'full' : 'lg'}
      dialogClassName="add-card-modal"
    >
      <Modal.Header>
        <Modal.Title>Add Card to Collection</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }} onKeyDown={handleKeyDown}>
        {/* Section 1: Search Bar */}
        <div style={{ marginBottom: SPACING.sectionGap }}>
          <Form.ControlLabel>Search Cards</Form.ControlLabel>
          <AutoComplete
            ref={searchInputRef}
            placeholder="Enter card name (2+ characters)"
            data={searchData}
            value={searchTerm}
            onChange={setSearchTerm}
            onSelect={handleSearchResultSelect}
            renderMenuItem={renderSearchMenuItem}
            renderMenu={renderSearchMenu}
          />
          {searchLoading && (
            <div style={{ textAlign: 'center', padding: SPACING.itemPadding }}>
              <Loader content="Searching..." />
            </div>
          )}
        </div>

        {/* Section 2: Printings + Card Details (Two-column layout, stacked on mobile) */}
        {selectedCard && (
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: SPACING.sectionGap, marginBottom: SPACING.sectionGap }}>
            {/* Left Column: Printings as Visual Cards */}
            <div style={{ width: isMobile ? '100%' : LAYOUT.leftColumnWidth, flexShrink: 0, maxHeight: isMobile ? 'none' : 'auto', paddingBottom: isMobile ? SPACING.sectionGap * 3 : 0 }}>
              <h4 style={{ marginBottom: SPACING.itemPadding, marginTop: 0 }}>Select Printing</h4>
              {loadingPrintings ? (
                <Loader content="Loading..." />
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACING.itemPadding,
                  overflowY: 'auto',
                  maxHeight: isMobile ? 200 : 300,
                  paddingRight: 8,
                }}>
                  {printings.map((printing) => {
                    const printingImage = getSmallImageUri(printing);
                    return (
                      <div
                        key={printing.id}
                        onClick={() => handleSelectPrinting(printing)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: SPACING.itemPadding,
                          backgroundColor: selectedPrinting?.id === printing.id ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                          border: selectedPrinting?.id === printing.id ? '3px solid #1976d2' : '1px solid #ddd',
                          borderRadius: 4,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {printingImage && (
                          <img
                            src={printingImage}
                            alt={`${printing.set} ${printing.collector_number}`}
                            style={{
                              width: PRINTING_IMAGE.width,
                              height: PRINTING_IMAGE.height,
                              borderRadius: PRINTING_IMAGE.borderRadius,
                              marginBottom: 8,
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <div style={{ fontSize: 11, fontWeight: 'bold', textAlign: 'center' }}>
                          {(printing.set || printing.set_abbrev || '').toUpperCase()}
                        </div>
                        <div style={{ fontSize: 10, color: '#888', textAlign: 'center' }}>
                          {printing.released_at ? new Date(printing.released_at).getFullYear() : ''}
                        </div>
                        {printing.rarity && (
                          <div style={{ fontSize: 9, marginTop: 4 }}>
                            • {printing.rarity}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Column: Card Details */}
            <div style={{
              flex: 1,
              minWidth: 0,
              width: isMobile ? '100%' : 'auto',
              paddingTop: isMobile ? SPACING.sectionGap * 2 : 0,
              borderTop: isMobile ? '1px solid #ddd' : 'none'
            }}>
              <h4 style={{ marginBottom: SPACING.itemPadding, marginTop: 0 }}>Card Details</h4>
              <Panel bordered style={{ padding: SPACING.itemPadding }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING.itemPadding }}>
                  <h3 style={{ margin: 0, flex: 1 }}>{selectedCard.name}</h3>
                  {/* Mana Cost */}
                  <div style={{ fontSize: 16, fontWeight: 'bold', minWidth: 80, textAlign: 'right' }}>
                    {getManaCost(selectedCard)}
                  </div>
                </div>

                {/* Type Line */}
                <div style={{ fontSize: FONT.typeLineSmall, marginBottom: SPACING.itemPadding, color: '#888' }}>
                  {selectedCard.type_line}
                </div>

                {/* Oracle Text */}
                <div style={{
                  fontSize: FONT.oracleSmall,
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                  marginBottom: SPACING.itemPadding,
                  padding: '8px',
                  backgroundColor: oracleTextBgColor,
                  color: oracleTextColor,
                  borderRadius: 4,
                  minHeight: 100,
                  maxHeight: 150,
                  overflowY: 'auto',
                }}>
                  {getOracleText(selectedCard) || 'No text.'}
                </div>

                {/* Stats - More Prominent */}
                <div style={{ marginTop: SPACING.itemPadding, paddingTop: SPACING.itemPadding, borderTop: '1px solid #ddd', display: 'flex', gap: SPACING.sectionGap }}>
                  {selectedCard.cmc !== undefined && (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#888', fontWeight: 'bold' }}>MV</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>{selectedCard.cmc}</div>
                    </div>
                  )}
                  {selectedCard.power && selectedCard.toughness && (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#888', fontWeight: 'bold' }}>P/T</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>{selectedCard.power}/{selectedCard.toughness}</div>
                    </div>
                  )}
                </div>

              </Panel>
            </div>
          </div>
        )}

        {!selectedCard && searchResults.length === 0 && searchTerm.length > 0 && (
          <Message type="info" showIcon>
            No cards found. Try a different search term.
          </Message>
        )}

        {!selectedCard && !searchTerm && (
          <Message type="info" showIcon>
            Start typing a card name to search
          </Message>
        )}

        {error && (
          <Message type="error" showIcon style={{ marginBottom: SPACING.itemPadding }}>
            {error}
          </Message>
        )}

        {warnings.length > 0 && (
          <Message type="warning" showIcon style={{ marginBottom: SPACING.itemPadding }}>
            <div>
              {warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          </Message>
        )}

        {/* Section 3: Add Form */}
        {selectedCard && (
          <div style={{ borderTop: '1px solid #ddd', paddingTop: SPACING.sectionGap, display: 'flex', flexDirection: 'column' }}>
            <h4 style={{ marginBottom: SPACING.itemPadding, flexShrink: 0 }}>Add to Collection</h4>

            {loadingCollections ? (
              <Loader content="Loading collections..." />
            ) : (
              <Form fluid style={{ overflow: 'hidden' }}>
                {/* Compact Form Row 1 */}
                <div style={{ display: isMobile ? 'block' : 'flex', gap: SPACING.itemPadding }}>
                  <div style={{ flex: 1 }}>
                    <Form.Group>
                      <Form.ControlLabel>Collection</Form.ControlLabel>
                      <SelectPicker
                        data={collectionOptions}
                        value={formData.collection_id}
                        onChange={(value) => setField('collection_id', value)}
                        searchable={false}
                        block
                        placeholder="Select a collection"
                        disabled={!!preSelectedCollectionId}
                      />
                    </Form.Group>
                  </div>
                  <div style={{ flex: 0.5 }}>
                    <Form.Group>
                      <Form.ControlLabel>Qty</Form.ControlLabel>
                      <InputNumber
                        value={formData.quantity}
                        onChange={(value) => {
                          const num = parseInt(value, 10);
                          setField('quantity', isNaN(num) ? 1 : num);
                        }}
                        min={0}
                        max={999}
                        style={{ width: '100%' }}
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* Compact Form Row 2 */}
                <div style={{ display: isMobile ? 'block' : 'flex', gap: SPACING.itemPadding }}>
                  <div style={{ flex: 1 }}>
                    <Form.Group>
                      <Form.ControlLabel>Condition</Form.ControlLabel>
                      <SelectPicker
                        data={CONDITION_OPTIONS}
                        value={formData.condition}
                        onChange={(value) => setField('condition', value)}
                        searchable={false}
                        block
                        placeholder="Select condition"
                      />
                    </Form.Group>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Group>
                      <Form.ControlLabel>Finish</Form.ControlLabel>
                      <SelectPicker
                        data={FINISH_OPTIONS}
                        value={formData.finish}
                        onChange={(value) => setField('finish', value)}
                        searchable={false}
                        block
                        placeholder="Select finish"
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* Special Options - Only show if eligible for the format */}
                <div style={{
                  display: 'flex',
                  gap: SPACING.itemPadding,
                  flexWrap: 'wrap',
                  marginBottom: SPACING.itemPadding,
                  minHeight: 32,
                }}>
                  {(isDeck || isMyCollection) && (
                    <Checkbox
                      checked={formData.is_proxy}
                      onChange={(_, checked) => {
                        setField('is_proxy', checked);
                        if (checked) setField('is_not_owned', false);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      🎨 Proxy Copy
                    </Checkbox>
                  )}

                  {isDeck && (
                    <Checkbox
                      checked={!formData.is_not_owned}
                      onChange={(_, checked) => {
                        setField('is_not_owned', !checked);
                        if (!checked) {
                          setField('purchase_price', '');
                          setField('is_proxy', false);
                        }
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      ✓ Owned
                    </Checkbox>
                  )}

                  {isCommander && selectedCard.type_line?.includes('Legendary') && selectedCard.type_line?.includes('Creature') && (
                    <Checkbox
                      checked={formData.is_commander}
                      onChange={(_, checked) => {
                        setField('is_commander', checked);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      👑 Commander
                    </Checkbox>
                  )}

                  {isOathbreaker && selectedCard.type_line?.includes('Planeswalker') && (
                    <Checkbox
                      checked={formData.is_commander}
                      onChange={(_, checked) => {
                        setField('is_commander', checked);
                        if (checked) setField('is_signature_spell', false);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      👑 Oathbreaker
                    </Checkbox>
                  )}

                  {isOathbreaker && selectedCard.type_line?.includes('Legendary') && selectedCard.type_line?.includes('Creature') && (
                    <Checkbox
                      checked={formData.is_commander}
                      onChange={(_, checked) => {
                        setField('is_commander', checked);
                        if (checked) setField('is_signature_spell', false);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      👑 Commander
                    </Checkbox>
                  )}

                  {isOathbreaker && selectedCard.type_line?.includes('Planeswalker') && (
                    <Checkbox
                      checked={formData.is_signature_spell}
                      onChange={(_, checked) => {
                        setField('is_signature_spell', checked);
                        if (checked) setField('is_commander', false);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      📜 Signature Spell
                    </Checkbox>
                  )}

                  {isBrawl && (selectedCard.type_line?.includes('Legendary') || selectedCard.type_line?.includes('Planeswalker')) && (
                    <Checkbox
                      checked={formData.is_commander}
                      onChange={(_, checked) => {
                        setField('is_commander', checked);
                      }}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      👑 Commander
                    </Checkbox>
                  )}

                  {hasSideboard && (
                    <Checkbox
                      checked={formData.is_sideboard}
                      onChange={(_, checked) => setField('is_sideboard', checked)}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      🎭 Sideboard
                    </Checkbox>
                  )}
                </div>

                {/* More Options Toggle */}
                <div style={{ marginBottom: SPACING.itemPadding }}>
                  <Button
                    appearance="link"
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    style={{ fontSize: 12, padding: 0 }}
                  >
                    {showMoreOptions ? '▼ Less options' : '▶ More options'}
                  </Button>
                </div>

                {/* Collapsible More Options */}
                {showMoreOptions && (
                  <>
                    <Form.Group>
                      <Form.ControlLabel>Purchase Price (optional)</Form.ControlLabel>
                      <InputNumber
                        value={formData.purchase_price}
                        onChange={(value) => setField('purchase_price', value)}
                        min={0}
                        step={0.01}
                        prefix="$"
                        style={{ width: '100%' }}
                        disabled={formData.is_not_owned}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.ControlLabel>Notes (optional)</Form.ControlLabel>
                      <Input
                        as="textarea"
                        rows={2}
                        value={formData.notes}
                        onChange={(value) => setField('notes', value)}
                        placeholder="Optional notes"
                      />
                    </Form.Group>
                  </>
                )}
              </Form>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          Cancel
        </Button>
        {selectedCard && selectedCollection && (
          <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
            Add Card
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddCardSidePanelModal;
